import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import { useMemo } from "react";
import { useAuth } from "../provider/authProvider";
import { ProtectedRoute } from "./ProtectedRoute";
import { ROLE_GROUPS } from "../constants/roles";

import Inventory from "../pages/inventory/Inventory";

import EvaluationLogin from "../pages/evaluation-system/Login";
import EvaluationSystem from "../pages/evaluation-system";
import CreditFlowReduced from "../pages/evaluation-system/CreditFlowReduced";

import CreditsPage from "../pages/Credits";
import CreditPage from "../pages/Credit";
import RegisterPage from "../pages/Register";
import VerifyClient from "../pages/Verify-client";
import PhonePage from "../pages/Phones";
import StockLookup from "../pages/StockLookup";
import UserRoles from "../pages/User-roles";
import FmsPage from "../pages/Fms";
import FmPage from "../pages/Fm";
import NotFound from "../pages/NotFound";
import Sedes from "../pages/Sedes";
import Receipts from "../pages/Receipts";
import SelectBranch from "../pages/SelectBranch";
import Analytics from "../pages/Analytics";
import Installment from "../pages/Installment";
import Receivables from "../pages/Receivables";
import CreditAnalysis from "../pages/CreditAnalysis";
import Cards from "../pages/Cards";
import Planning from "../pages/Planning";
import Comisiones from "../pages/Comisiones";
import FmStats from "../pages/FmStats";
import FeeStats from "../pages/FeeStats";
import Semaforo from "../pages/Semaforo";
import SmsCampaigns from "../pages/SmsCampaigns";
import Warranties from "../pages/Warranties";
import PayjoyAudit from "../pages/PayjoyAudit";
import Reports from "../pages/Reports";
import Academy from "../pages/Academy";

import AccountingConfig from "../pages/Accounting/AccountingConfig";
import AccountingDashboard from "../pages/Accounting/AccountingDashboard";
import AccountingMovements from "../pages/Accounting/AccountingMovements";
import AccountingScheduled from "../pages/Accounting/AccountingScheduled";


import UserValidation from "../pages/evaluation-system/UserValidation";
import CardsInitialValidation from "../pages/cards/CardsInitialValidation";

/*
|--------------------------------------------------------------------------
| RUTA PÚBLICA DE LOGIN
|--------------------------------------------------------------------------
*/
const SelectBranchGate = () => {
  const { rol, token, isAuthReady } = useAuth();

  if (!isAuthReady) return null;

  const currentToken = token || localStorage.getItem("token");
  if (!currentToken) {
    return <Navigate to="/evaluation-system-verify/login" replace />;
  }

  // La sede del asesor (rol 3) se administra exclusivamente desde Usuarios.
  // Nunca se expone el selector manual, ni siquiera accediendo por URL.
  if (rol == null) return null;
  if (String(rol) === "3") {
    return <Navigate to="/evaluation-system" replace />;
  }

  return <SelectBranch />;
};

const PublicOnly = () => {
  const { token, isAuthReady } = useAuth();

  if (!isAuthReady) return null;

  const currentToken = token || localStorage.getItem("token");

  return currentToken ? (
    <Navigate to="/evaluation-system" replace />
  ) : (
    <EvaluationLogin />
  );
};

/*
|--------------------------------------------------------------------------
| HELPER PARA RUTAS PRIVADAS POR ROL
|--------------------------------------------------------------------------
*/
const withRole = (path, pageElement, allowedRoles) => ({
  path,
  element: <ProtectedRoute allowedRoles={allowedRoles} />,
  children: [
    {
      index: true,
      element: pageElement,
    },
  ],
});

/*
|--------------------------------------------------------------------------
| VALIDACIÓN PÚBLICA
|--------------------------------------------------------------------------
*/
const PublicValidationGate = () => {
  const params = new URLSearchParams(window.location.search);
  const pathname = String(window.location.pathname || "");

  const hasPublicValidation =
    params.get("publicValidation") === "1" ||
    params.get("validationToken") ||
    params.get("publicValidationToken") ||
    pathname.startsWith("/user-validation");

  if (hasPublicValidation) {
    return <UserValidation />;
  }

  return <ProtectedRoute />;
};

/*
|--------------------------------------------------------------------------
| ROUTER PRINCIPAL
|--------------------------------------------------------------------------
*/
const Routes = () => {
  const routes = useMemo(() => {
    /*
    |--------------------------------------------------------------------------
    | EVALUACIÓN
    |--------------------------------------------------------------------------
    */
    const evaluationRoutes = [
      {
        path: "/evaluation-system",
        element: <EvaluationSystem />,
      },

      withRole(
        "/evaluation-system/credit-flow",
        <CreditFlowReduced />,
        ROLE_GROUPS.MAIN,
      ),

      {
        path: "/credit-flow",
        element: (
          <Navigate to="/evaluation-system/credit-flow" replace />
        ),
      },
    ];

    /*
    |--------------------------------------------------------------------------
    | RUTAS PRIVADAS
    |--------------------------------------------------------------------------
    */
    const privateRoutes = [
      /*
      |--------------------------------------------------------------------------
      | SELECCIÓN DE SEDE
      |--------------------------------------------------------------------------
      */
      {
        path: "/select-branch",
        element: <SelectBranchGate />,
      },

      /*
      |--------------------------------------------------------------------------
      | EVALUACIÓN
      |--------------------------------------------------------------------------
      */
      ...evaluationRoutes,

      /*
      |--------------------------------------------------------------------------
      | CRÉDITOS
      |--------------------------------------------------------------------------
      */
      withRole("/receivables", <Receivables />, [1,8,10]),
      withRole(
        "/credits",
        <CreditsPage namePage="creditsPage" />,
        ROLE_GROUPS.CREDIT_TEAM,
      ),

      withRole(
        "/credit/:id",
        <CreditPage namePage="creditsPage" />,
        ROLE_GROUPS.CREDIT_INT_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | REGISTROS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/register",
        <RegisterPage namePage="registerPage" />,
        ROLE_GROUPS.REGISTER_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | TARJETAS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/cards",
        <Cards namePage="cardsPage" />,
        ROLE_GROUPS.CARDS_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | VERIFICACIÓN
      |--------------------------------------------------------------------------
      */
      withRole(
        "/verify-client",
        <VerifyClient namePage="verifyPage" />,
        ROLE_GROUPS.VERIFY_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | STOCK ANTERIOR
      |--------------------------------------------------------------------------
      */
      withRole(
        "/stock",
        <StockLookup namePage="stockPage" />,
        ROLE_GROUPS.STOCK_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | FINANCIAMIENTO MIXTO
      |--------------------------------------------------------------------------
      */
      withRole(
        "/fms",
        <FmsPage namePage="fmsPage" />,
        ROLE_GROUPS.FM_TEAM,
      ),

      withRole(
        "/fm/:id",
        <FmPage namePage="fmsPage" />,
        ROLE_GROUPS.FM_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | CUOTAS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/installment",
        <Installment namePage="installmentPage" />,
        ROLE_GROUPS.INSTALLMENT_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | RECIBOS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/receipts",
        <Receipts namePage="receiptsPage" />,
        ROLE_GROUPS.RECEIPTS_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | USUARIOS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/user-roles",
        <UserRoles namePage="userPage" />,
        ROLE_GROUPS.ADMIN_ONLY,
      ),

      /*
      |--------------------------------------------------------------------------
      | SEDES
      |--------------------------------------------------------------------------
      */
      withRole(
        "/sedes",
        <Sedes namePage="sedePage" />,
        ROLE_GROUPS.ADMIN_ONLY,
      ),

      /*
      |--------------------------------------------------------------------------
      | ANÁLISIS DE CRÉDITOS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/credit-analysis",
        <CreditAnalysis namePage="creditAnalysisPage" />,
        ROLE_GROUPS.ADMIN_ONLY,
      ),

      /*
      |--------------------------------------------------------------------------
      | ANALÍTICA
      |--------------------------------------------------------------------------
      */
      withRole(
        "/analytics",
        <Analytics namePage="analytics" />,
        ROLE_GROUPS.ADMIN_ONLY,
      ),

      /*
      |--------------------------------------------------------------------------
      | PLANIFICACIÓN
      |--------------------------------------------------------------------------
      */
      withRole(
        "/planning",
        <Planning namePage="planningPage" />,
        ROLE_GROUPS.PLANNING_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | COMISIONES
      |--------------------------------------------------------------------------
      */
      withRole(
        "/comisiones",
        <Comisiones namePage="comisionesPage" />,
        ROLE_GROUPS.COMISIONES_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | ESTADÍSTICAS FM
      |--------------------------------------------------------------------------
      */
      withRole(
        "/fm-stats",
        <FmStats namePage="fmstatsPage" />,
        ROLE_GROUPS.FMSTATS_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | ESTADÍSTICAS DE CUOTAS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/fee-stats",
        <FeeStats namePage="feestatsPage" />,
        ROLE_GROUPS.FEESTATS_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | SEMÁFORO
      |--------------------------------------------------------------------------
      */
      withRole(
        "/semaforo",
        <Semaforo namePage="semaforoPage" />,
        ROLE_GROUPS.SEMAFORO_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | CAMPAÑAS SMS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/sms-campaigns",
        <SmsCampaigns namePage="smsCampaignsPage" />,
        ROLE_GROUPS.SMS_CAMPAIGNS,
      ),

      /*
      |--------------------------------------------------------------------------
      | GARANTÍAS
      |--------------------------------------------------------------------------
      */
      withRole(
        "/warranties",
        <Warranties namePage="warrantiesPage" />,
        ROLE_GROUPS.WARRANTIES_TEAM,
      ),

      /*
      |--------------------------------------------------------------------------
      | AUDITORÍA PAYJOY
      |--------------------------------------------------------------------------
      */
      withRole(
        "/payjoy-audit",
        <PayjoyAudit namePage="payjoyAuditPage" />,
        ROLE_GROUPS.PAYJOY_AUDIT,
      ),

      /*
      |--------------------------------------------------------------------------
      | REPORTES
      |--------------------------------------------------------------------------
      */
      withRole(
        "/reports",
        <Reports namePage="reportsPage" />,
        ROLE_GROUPS.REPORTS_TEAM,
      ),
      withRole(
        "/academy",
        <Academy namePage="academyPage" />,
        [1, 3, 8],
      ),

      ...[
        ["/inventory", "inventoryDashboardPage"],
        ["/inventory/logistics-receipts", "inventoryReceiptsPage"],
        ["/inventory/catalogs", "inventoryCatalogsPage"],
        ["/inventory/products", "inventoryProductsPage"],
        ["/inventory/entries", "inventoryEntriesPage"],
        ["/inventory/purchase-invoices", "inventoryPurchaseInvoicesPage"],
        ["/inventory/stock", "inventoryStockPage"],
        ["/inventory/transfers", "inventoryTransfersPage"],
        ["/inventory/shipping-guides", "inventoryShippingGuidesPage"],
        ["/inventory/traceability", "inventoryTracePage"],
        ["/inventory/audit", "inventoryAuditPage"],
        ["/inventory/locations", "inventoryLocationsPage"],
        ["/inventory/documents", "inventorySaleReceiptsPage"],
        ["/inventory/promotions", "inventoryPromotionsPage"],
      ].map(([path, namePage]) =>
        withRole(
          path,
          <Inventory namePage={namePage} />,
          [1, 7, 8],
        ),
      ),

      /*
      |--------------------------------------------------------------------------
      | CONTABILIDAD
      |--------------------------------------------------------------------------
      */
      withRole(
        "/accounting",
        <AccountingMovements namePage="accountingPage" />,
        ROLE_GROUPS.ACCOUNTING_TEAM,
      ),

      withRole(
        "/accounting/movements",
        <AccountingMovements namePage="accountingPage" />,
        ROLE_GROUPS.ACCOUNTING_TEAM,
      ),

      withRole(
        "/accounting/dashboard",
        <AccountingDashboard namePage="accountingDashboardPage" />,
        ROLE_GROUPS.ACCOUNTING_TEAM,
      ),

      withRole(
        "/accounting/config",
        <AccountingConfig namePage="accountingConfigPage" />,
        ROLE_GROUPS.ACCOUNTING_TEAM,
      ),

      withRole(
        "/accounting/scheduled",
        <AccountingScheduled namePage="accountingScheduledPage" />,
        ROLE_GROUPS.ACCOUNTING_TEAM,
      )
    ];

    /*
    |--------------------------------------------------------------------------
    | ESTRUCTURA GENERAL DE RUTAS
    |--------------------------------------------------------------------------
    */
    return [
      {
        path: "/about-us",
        element: <div>About Us</div>,
      },

      {
        path: "/evaluation-system-verify/login",
        element: <PublicOnly />,
      },

      /*
      |--------------------------------------------------------------------------
      | VALIDACIÓN PÚBLICA DE INICIAL
      |--------------------------------------------------------------------------
      */
      {
        path: "/cards-initial-validation/:token",
        element: <CardsInitialValidation />,
      },

      /*
      |--------------------------------------------------------------------------
      | VALIDACIÓN FACIAL PÚBLICA
      |--------------------------------------------------------------------------
      */
      {
        path: "/user-validation/:validationToken",
        element: <UserValidation />,
      },

      {
        path: "/user-validation",
        element: <UserValidation />,
      },

      /*
      |--------------------------------------------------------------------------
      | RUTAS PRIVADAS
      |--------------------------------------------------------------------------
      */
      {
        path: "/",
        element: <PublicValidationGate />,
        children: privateRoutes,
      },

      /*
      |--------------------------------------------------------------------------
      | PÁGINA NO ENCONTRADA
      |--------------------------------------------------------------------------
      */
      {
        path: "*",
        element: <NotFound />,
      },
    ];
  }, []);

  const router = useMemo(
    () => createBrowserRouter(routes),
    [routes],
  );

  return <RouterProvider router={router} />;
};

export default Routes;
